import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Commonservice } from '../../../services/commonservice';
import { Httpservice } from '../../../services/httpservice';
import { NotificationRefreshService } from '../../../services/notification-refresh';
import { SessionService } from '../../../services/session.service';
import { ServerTimeService } from '../../../services/server-time';
import { ClickOutsideDirective } from '../../../helper/directives/click-outside.directive';
import { DashboardHeaderUser } from './header.interfaces';

@Component({
  selector: 'app-dashboard-header',
  standalone: true,
  imports: [CommonModule, ClickOutsideDirective],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class DashboardHeaderComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input() title = 'Nepal Load Dispatch Center';
  @Input() subtitle = 'National Power Grid Management';
  @Input({ required: true }) user!: DashboardHeaderUser;
  @Input() currentTime = '';

  @Output() menuToggle = new EventEmitter<void>();
  @HostBinding('class.header--scrolled') protected headerScrolled = false;
  @HostBinding('style.--header-sticky-offset.px') protected headerStickyOffset = 0;

  protected showProfileMenu = false;
  protected showNotifications = false;
  protected notificationLoading = false;
  protected notificationActionLoading = false;
  protected notifications: UserNotification[] = [];
  protected unreadNotificationCount = 0;

  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private scrollContainer: HTMLElement | null = null;
  private readonly scrollListener = (): void => this.updateStickyState();
  private readonly resizeListener = (): void => this.syncStickyOffset();

  protected get displayUser(): DashboardHeaderUser {
    const sessionProfile = this.sessionSvc.getProfileDetails();
    const sessionFullName = sessionProfile?.user_fullname?.trim() || this.user.fullName;
    const profileImageUrl = sessionProfile?.profile_image?.uploaded_file || this.user.profileImageUrl || null;
    const roleLabel = sessionProfile?.role_display_name?.trim() || sessionProfile?.role_name?.trim() || this.user.roleLabel;

    return {
      ...this.user,
      fullName: sessionFullName,
      initials: this.buildInitials(sessionFullName),
      roleLabel,
      profileImageUrl,
    };
  }

  protected get currentTimeLabel(): string {
    return this.serverTimeSvc.getCurrentTimeLabel();
  }

  protected get darkModeEnabled(): boolean {
    return this.preferencesSvc.darkMode;
  }

  protected get showBsDateEnabled(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected handleMenuToggle(): void {
    this.menuToggle.emit();
  }

  protected toggleProfileMenu(): void {
    this.showProfileMenu = !this.showProfileMenu;
    this.showNotifications = false;
  }

  protected closeNotifications(): void {
    this.showNotifications = false;
  }

  protected closeProfileMenu(): void {
    this.showProfileMenu = false;
  }

  protected toggleNotifications(): void {
    this.showNotifications = !this.showNotifications;
    this.showProfileMenu = false;
    if (this.showNotifications) {
      this.loadNotifications();
    }
  }

  protected handleNotificationClick(notification: UserNotification): void {
    this.httpSvc
      .requestCall(`${AuthEndPoints.MARK_USER_NOTIFICATION_READ}/${notification.id}/read`, ApiMethod.PATCH, {}, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notifications = this.notifications.filter((item) => item.id !== notification.id);
          this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1);
          this.showNotifications = false;
          this.notificationRefreshSvc.notify();
          this.redirectToNotification(notification.redirect_url);
        },
      });
  }

  protected markAllNotificationsRead(): void {
    if (!this.unreadNotificationCount || this.notificationActionLoading) {
      return;
    }

    this.notificationActionLoading = true;
    this.httpSvc
      .requestCall(AuthEndPoints.MARK_ALL_USER_NOTIFICATIONS_READ, ApiMethod.PATCH, {}, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notifications = [];
          this.unreadNotificationCount = 0;
          this.notificationActionLoading = false;
          this.notificationRefreshSvc.notify();
        },
        error: () => {
          this.notificationActionLoading = false;
        },
      });
  }

  protected viewAllNotifications(): void {
    this.showNotifications = false;
    this.router.navigate(['/dashboard/notifications']);
  }

  protected getNotificationDescription(description: string | null): string {
    if (!description) {
      return '';
    }

    const words = description.trim().split(/\s+/).filter(Boolean);
    const visibleWordCount = 10;
    if (words.length <= visibleWordCount) {
      return description;
    }

    return `${words.slice(0, visibleWordCount).join(' ')} ...`;
  }

  protected getNotificationRelativeTime(createdAt: string | null): string {
    const notificationTime = this.parseNotificationTime(createdAt);
    if (!notificationTime) {
      return '-';
    }

    const currentTime = this.serverTimeSvc.getCurrentTime() ?? new Date();
    const elapsedSeconds = Math.max(0, Math.floor((currentTime.getTime() - notificationTime.getTime()) / 1000));
    if (elapsedSeconds < 60) {
      return 'just now';
    }

    const units = [
      { seconds: 31536000, label: 'year' },
      { seconds: 2592000, label: 'month' },
      { seconds: 86400, label: 'day' },
      { seconds: 3600, label: 'hour' },
      { seconds: 60, label: 'minute' },
    ];
    const unit = units.find((candidate) => elapsedSeconds >= candidate.seconds) ?? { seconds: 1, label: 'second' };
    const value = Math.floor(elapsedSeconds / unit.seconds);
    return `${value} ${unit.label}${value === 1 ? '' : 's'} ago`;
  }

  protected getNotificationTimeTooltip(notification: UserNotification): string {
    if (this.preferencesSvc.showBsDate && notification.created_at_bs) {
      if (/\d{2}:\d{2}$/.test(notification.created_at_bs)) {
        return notification.created_at_bs;
      }

      const adTime = /\d{2}:\d{2}$/.exec(notification.created_at_ad ?? '')?.[0];
      return adTime ? `${notification.created_at_bs} ${adTime}` : notification.created_at_bs;
    }

    if (notification.created_at_ad) {
      return notification.created_at_ad;
    }

    const notificationTime = this.parseNotificationTime(notification.created_at);
    if (!notificationTime) {
      return notification.created_at_ad ?? '-';
    }

    const day = String(notificationTime.getDate()).padStart(2, '0');
    const month = String(notificationTime.getMonth() + 1).padStart(2, '0');
    const year = notificationTime.getFullYear();
    const hour = String(notificationTime.getHours()).padStart(2, '0');
    const minute = String(notificationTime.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hour}:${minute}`;
  }

  private parseNotificationTime(createdAt: string | null): Date | null {
    if (!createdAt) {
      return null;
    }

    const parsed = new Date(createdAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  protected toggleDarkMode(): void {
    this.preferencesSvc.setDarkMode(!this.preferencesSvc.darkMode);
  }

  protected toggleShowBsDate(enabled?: boolean): void {
    this.preferencesSvc.setShowBsDate(typeof enabled === 'boolean' ? enabled : !this.preferencesSvc.showBsDate);
  }

  protected handleMyProfile(): void {
    this.showProfileMenu = false;
    this.router.navigate(['/dashboard/my-profile']);
  }

  protected handleSettings(): void {
    this.showProfileMenu = false;
    this.router.navigate(['/dashboard/settings']);
  }

  constructor(
    private readonly commonSvc: Commonservice,
    private readonly httpSvc: Httpservice,
    private readonly router: Router,
    private readonly sessionSvc: SessionService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly notificationRefreshSvc: NotificationRefreshService,
  ) {}

  ngOnInit(): void {
    this.notificationRefreshSvc.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadNotifications());
    this.loadNotifications();
  }

  ngAfterViewInit(): void {
    this.scrollContainer = this.findScrollableParent();
    this.syncStickyOffset();
    this.updateStickyState();

    if (this.scrollContainer instanceof HTMLElement) {
      this.scrollContainer.addEventListener('scroll', this.scrollListener, { passive: true });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeListener, { passive: true });
    }
  }

  ngOnDestroy(): void {
    if (this.scrollContainer instanceof HTMLElement) {
      this.scrollContainer.removeEventListener('scroll', this.scrollListener);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  protected async handleLogout(): Promise<void> {
    this.showProfileMenu = false;
    await this.commonSvc.logout();
    this.router.navigate(['/signin']);
  }

  private buildInitials(fullName: string): string {
    const initials = fullName
      .split(' ')
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');

    return initials || this.user.initials;
  }

  private loadNotifications(): void {
    this.notificationLoading = true;
    this.httpSvc
      .requestCall(AuthEndPoints.GET_USER_NOTIFICATIONS, ApiMethod.GET, '', false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const value = response?.value ?? {};
          this.notifications = Array.isArray(value.notifications) ? value.notifications : [];
          this.unreadNotificationCount = Number(value.unread_count ?? this.notifications.length) || 0;
          this.notificationLoading = false;
        },
        error: () => {
          this.notificationLoading = false;
        },
      });
  }

  private redirectToNotification(redirectUrl: string | null): void {
    if (!redirectUrl) {
      return;
    }

    if (/^https?:\/\//i.test(redirectUrl)) {
      window.location.assign(redirectUrl);
      return;
    }

    this.router.navigateByUrl(redirectUrl);
  }

  private findScrollableParent(): HTMLElement | null {
    if (typeof window === 'undefined') {
      return null;
    }

    let parent = this.hostElement.nativeElement.parentElement;
    while (parent) {
      const styles = window.getComputedStyle(parent);
      const isScrollable = /(auto|scroll|overlay)/.test(styles.overflowY) && parent.scrollHeight > parent.clientHeight;
      if (isScrollable) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return null;
  }

  private syncStickyOffset(): void {
    const host = this.hostElement.nativeElement;
    if (this.scrollContainer instanceof HTMLElement) {
      const containerRect = this.scrollContainer.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      this.headerStickyOffset = Math.max(0, Math.round(hostRect.top - containerRect.top));
      return;
    }

    this.headerStickyOffset = Math.max(0, Math.round(host.getBoundingClientRect().top));
  }

  private updateStickyState(): void {
    if (this.scrollContainer instanceof HTMLElement) {
      this.headerScrolled = this.scrollContainer.scrollTop > 0;
      return;
    }
    this.headerScrolled = false;
  }
}

interface UserNotification {
  id: number;
  title: string;
  description: string | null;
  status: string | null;
  redirect_url: string | null;
  is_read: boolean;
  created_at: string | null;
  created_at_ad: string | null;
  created_at_bs: string | null;
}
